// ===================================================================
// ERROR HANDLING UTILITIES
// ===================================================================

/**
 * Enhanced error handler for Supabase operations with better network handling
 */
export const handleSupabaseError = (error) => {
  console.error('Supabase operation failed:', error);

  // Network errors - most common issue with enhanced detection
  if (error?.message?.includes('Failed to fetch') || 
      error?.message?.includes('Network connection failed') ||
      error?.message?.includes('Service temporarily unavailable') ||
      error?.message?.includes('No internet connection detected') ||
      error?.name === 'TypeError'|| error?.message?.includes('fetch') ||
      error?.message?.includes('NetworkError')) {
    return {
      type: 'network',
      message: 'Connection issue detected. Please check your internet connection and try again.',
      originalError: error,
      retryable: true,
      userFriendly: true,
      severity: 'warning'
    };
  }

  // Timeout errors with enhanced handling
  if (error?.name === 'AbortError' || error?.message?.includes('timeout') ||
      error?.message?.includes('Request timeout') ||
      error?.message?.includes('timed out')) {
    return {
      type: 'timeout',
      message: 'Request timed out. Your connection may be slow - please try again.',
      originalError: error,
      retryable: true,
      userFriendly: true,
      severity: 'warning'
    };
  }

  // Circuit breaker / rate limiting
  if (error?.message?.includes('Circuit breaker') ||
      error?.message?.includes('too many recent failures') ||
      error?.message?.includes('rate limit') ||
      error?.message?.includes('too many requests')) {
    return {
      type: 'rate_limit',
      message: 'Service is temporarily busy. Please wait a moment and try again.',
      originalError: error,
      retryable: true,
      userFriendly: true,
      severity: 'warning'
    };
  }

  // Authentication errors
  if (error?.message?.includes('JWT') || 
      error?.message?.includes('Invalid login credentials') ||
      error?.message?.includes('User not found') ||
      error?.message?.includes('Session expired')) {
    return {
      type: 'auth',
      message: 'Authentication failed. Please log in again.',
      originalError: error,
      retryable: false,
      userFriendly: true,
      severity: 'error'
    };
  }

  // Database errors with enhanced mapping
  if (error?.code) {
    const errorMappings = {
      '23505': 'This record already exists.',
      '23503': 'Referenced record does not exist.',
      '42703': 'Database field not found.',
      '42P01': 'Database table not found.',
      'PGRST116': 'No data found for this request.',
      'PGRST301': 'You do not have permission to perform this action.',
      'PGRST204': 'Invalid request format.',
      '08006': 'Database connection failed.',
      '08001': 'Unable to connect to database server.'
    };

    return {
      type: 'database',
      message: errorMappings[error.code] || `Database error: ${error?.message || 'Unknown error'}`,
      code: error?.code,
      originalError: error,
      retryable: ['08006', '08001'].includes(error?.code),
      userFriendly: true,
      severity: ['08006', '08001'].includes(error?.code) ? 'warning' : 'error'
    };
  }

  // Default error with better fallback
  return {
    type: 'unknown',
    message: error?.message || 'Something went wrong. Please try again.',
    originalError: error,
    retryable: true,
    userFriendly: true,
    severity: 'error'
  };
};

/**
 * Enhanced retryable function wrapper with exponential backoff
 */
export const createRetryableFunction = (fn, maxRetries = 3, baseDelay = 1000) => {
  return async (...args) => {
    let lastError;
    let currentDelay = baseDelay;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await fn(...args);
        
        // Check if the result indicates a retryable error
        if (result?.error && isRetryableError(result?.error)) {
          throw result?.error;
        }
        
        return result;
      } catch (error) {
        lastError = error;
        
        const errorInfo = handleSupabaseError(error);
        
        // Don't retry if it's not a retryable error or we're on the last attempt
        if (!errorInfo?.retryable || attempt === maxRetries) {
          break;
        }

        // Log retry attempt
        console.warn(`Retry attempt ${attempt}/${maxRetries} in ${Math.round(currentDelay)}ms:`, errorInfo?.message);
        
        // Wait before retry with exponential backoff + jitter
        await new Promise(resolve => setTimeout(resolve, currentDelay + Math.random() * 1000));
        currentDelay *= 2; // Exponential backoff
      }
    }

    // Return the error in Supabase format if all retries failed
    const errorInfo = handleSupabaseError(lastError);
    return { 
      data: null, 
      error: { 
        message: errorInfo?.message,
        type: errorInfo?.type,
        retryable: errorInfo?.retryable,
        severity: errorInfo?.severity
      } 
    };
  };
};

/**
 * Enhanced retryable error detection
 */
const isRetryableError = (error) => {
  const retryableMessages = [
    'Failed to fetch',
    'Network connection failed',
    'Network request failed',
    'Service temporarily unavailable',
    'No internet connection detected',
    'timeout',
    'timed out',
    'ENOTFOUND',
    'ECONNREFUSED',
    'ETIMEDOUT',
    'Request timeout',
    'Circuit breaker'
  ];

  const errorMessage = error?.message?.toLowerCase() || '';
  return retryableMessages?.some(msg => errorMessage?.includes(msg?.toLowerCase())) ||
         error?.name === 'AbortError' ||
         error?.name === 'TypeError' ||
         (error?.code && ['08006', '08001'].includes(error?.code));
};

/**
 * Enhanced connection status checker with multiple fallback methods
 */
export const checkConnection = async () => {
  try {
    // Method 1: Try to fetch favicon (most reliable for same-origin)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller?.abort(), 5000);

    const response = await fetch('/favicon.ico', {
      method: 'HEAD',
      signal: controller?.signal,
      cache: 'no-cache'
    });

    clearTimeout(timeoutId);
    return response?.ok || response?.status === 304; // 304 Not Modified is also OK
  } catch (error) {
    console.warn('Primary connection check failed, trying alternative method');
    
    try {
      // Method 2: Try to fetch manifest.json
      const response = await fetch('/manifest.json', {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(3000)
      });
      return response?.ok || response?.status === 304;
    } catch {
      // Method 3: Check navigator.onLine as last resort
      console.warn('All connection checks failed, using navigator.onLine');
      return navigator?.onLine !== false;
    }
  }
};

/**
 * Enhanced network status utilities with better monitoring
 */
export const networkStatus = {
  isOnline: () => navigator?.onLine !== false,
  
  checkConnection,
  
  onConnectionChange: (callback) => {
    const handleOnline = async () => {
      console.log('Network connection event: online');
      // Double-check with actual connection test
      const isActuallyConnected = await checkConnection();
      callback(isActuallyConnected);
    };
    
    const handleOffline = () => {
      console.warn('Network connection event: offline');
      callback(false);
    };

    window?.addEventListener('online', handleOnline);
    window?.addEventListener('offline', handleOffline);
    
    // Also monitor page visibility changes for better detection
    const handleVisibilityChange = async () => {
      if (!document?.hidden && navigator?.onLine !== false) {
        const isConnected = await checkConnection();
        callback(isConnected);
      }
    };
    
    document?.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window?.removeEventListener('online', handleOnline);
      window?.removeEventListener('offline', handleOffline);
      document?.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }
};

/**
 * Enhanced retry mechanism with connection checks and circuit breaker
 */
export const retryWithConnectionCheck = async (operation, options = {}) => {
  const { 
    maxRetries = 3, 
    baseDelay = 1000, 
    checkConnection: shouldCheck = true,
    circuitBreakerThreshold = 5 
  } = options;
  
  let consecutiveFailures = 0;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Circuit breaker: if too many consecutive failures, fail fast
      if (consecutiveFailures >= circuitBreakerThreshold) {
        throw new Error('Circuit breaker active - too many consecutive failures');
      }
      
      // Check connection before retry (except first attempt)
      if (attempt > 1 && shouldCheck) {
        const isConnected = await checkConnection();
        if (!isConnected) {
          throw new Error('No internet connection detected');
        }
      }

      const result = await operation();
      
      // Success - reset failure count
      consecutiveFailures = 0;
      return result;
      
    } catch (error) {
      consecutiveFailures++;
      const errorInfo = handleSupabaseError(error);

      if (!errorInfo?.retryable || attempt === maxRetries) {
        throw error;
      }

      const delay = baseDelay * Math.pow(2, attempt - 1) + Math.random() * 1000;
      console.warn(`Retry ${attempt}/${maxRetries} after ${Math.round(delay)}ms:`, errorInfo?.message);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

/**
 * Debounced error handler to prevent error spam
 */
const errorHistory = new Map();

export const debouncedErrorHandler = (error, key = 'default', debounceTime = 5000) => {
  const now = Date?.now();
  let lastError = errorHistory?.get(key);

  if (lastError && (now - lastError?.timestamp) < debounceTime) {
    return lastError?.errorInfo; // Return cached error info instead of processing again
  }

  const errorInfo = handleSupabaseError(error);
  errorHistory?.set(key, { timestamp: now, error, errorInfo });
  
  console.error(`Debounced error [${key}]:`, errorInfo);
  
  return errorInfo;
};

/**
 * Enhanced global error boundary with better network error handling
 */
export const setupGlobalErrorHandling = () => {
  // Handle unhandled promise rejections
  window?.addEventListener?.('unhandledrejection', (event) => {
    const errorInfo = handleSupabaseError(event?.reason);
    console.error('Unhandled promise rejection:', errorInfo);
    
    // Prevent default browser error handling for network errors to avoid spam
    if (errorInfo?.type === 'network' || errorInfo?.type === 'timeout') {
      event?.preventDefault?.();
    }
  });

  // Handle general errors
  window?.addEventListener?.('error', (event) => {
    const errorInfo = handleSupabaseError(event?.error);
    
    // Only log significant errors to avoid console spam
    if (errorInfo?.severity !== 'warning' || errorInfo?.type !== 'network') {
      console.error('Global error caught:', errorInfo);
    }
  });
};

/**
 * User-friendly error display with severity-based handling
 */
export const showUserError = (error, options = {}) => {
  const errorInfo = handleSupabaseError(error);
  
  // Only log errors, not warnings to reduce console noise
  if (errorInfo?.severity === 'error') {
    console.error('User-facing error:', errorInfo?.message);
  } else {
    console.warn('User-facing warning:', errorInfo?.message);
  }
  
  if (options?.throwIfNotUserFriendly && !errorInfo?.userFriendly) {
    throw error;
  }
  
  return errorInfo;
};

/**
 * Initialize enhanced error handling system
 */
export const initializeErrorHandling = () => {
  setupGlobalErrorHandling();
  
  // Clean up error history more frequently to prevent memory leaks
  setInterval(() => {
    const now = Date?.now();
    for (const [key, value] of errorHistory?.entries()) {
      if (now - value?.timestamp > 30000) { // 30 seconds instead of 60
        errorHistory?.delete(key);
      }
    }
  }, 30000);
  
  console.log('Enhanced error handling system initialized');
};

// Export all error handling utilities
export default {
  handleSupabaseError,
  createRetryableFunction,
  checkConnection,
  retryWithConnectionCheck,
  setupGlobalErrorHandling,
  debouncedErrorHandler,
  initializeErrorHandling,
  networkStatus,
  showUserError
};